import React, { useState, useMemo, useEffect, useContext, createContext } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Home, Layers, Paintbrush, Droplet, Shield,
  Plus, Trash2, Save, Send, MapPin, FileText
} from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import API_URL from '@/config/api';
import {
  PRIMERS as _PRIMERS, FINISHES as _FINISHES, PIGMENTS as _PIGMENTS,
  WAXES as _WAXES, SEALERS as _SEALERS,
  PRODECOR_PRIMARY, PRODECOR_SECONDARY,
} from '@/data/prodecorProducts';
import { pickPacks } from '@/utils/packPicker';
import PrioritySelector from '@/components/PrioritySelector';
import { formatApiError } from '@/utils/apiError';
import VoucherRail from '@/components/vouchers/VoucherRail';

// Normalise a name the same way the backend does (case + whitespace).
const _norm = (s) => (s || '').toLowerCase().split(/\s+/).join(' ').trim();

// Apply a `matchName → live price` map to the catalogue, falling back to the
// hardcoded ex-VAT price when a SKU isn't in the pricelist yet.
const _applyLive = (catalogue, priceMap) => {
  const resolve = (matchName, fallback) => {
    if (!matchName || !priceMap) return fallback;
    const v = priceMap[_norm(matchName)];
    return v != null ? v : fallback;
  };
  if (Array.isArray(catalogue)) {
    return catalogue.map((product) => ({
      ...product,
      packs: product.packs.map((pack) => ({
        ...pack,
        price: resolve(pack.matchName, pack.price),
      })),
    }));
  }
  // PIGMENTS is a plain object keyed by size id.
  const out = {};
  Object.entries(catalogue).forEach(([k, v]) => {
    out[k] = { ...v, price: resolve(v.matchName, v.price) };
  });
  return out;
};

// Context so AreaCard (and any future child) reads live-priced catalogues
// without prop-drilling.
const LivePricingCtx = createContext({
  PRIMERS: _PRIMERS, FINISHES: _FINISHES, PIGMENTS: _PIGMENTS,
  WAXES: _WAXES, SEALERS: _SEALERS, tier: null, loaded: false,
});
const useLive = () => useContext(LivePricingCtx);

const fmtGBP = (n) =>
  new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', maximumFractionDigits: 2 }).format(n || 0);

const blankArea = () => ({
  id: crypto.randomUUID(),
  name: '',
  areaM2: '',
  wastagePct: 10,
  finishId: null,
  pigmentSize: null,
  pigmentQty: 1,
  pigmentColour: 'Black',
  protectionType: null, // 'wax' | 'sealer'
  waxId: null,
  sealerId: null,
  sealerVariant: null, // for Polyurethane Shield: Gloss/Satin/Matt
});

const PackList = ({ result, label }) => {
  if (!result || !result.picks || !result.picks.length) return null;
  return (
    <div className="mt-2 text-xs text-gray-700 space-y-0.5">
      {label && <div className="font-semibold uppercase tracking-wider text-gray-500 text-[10px]">{label}</div>}
      {result.picks.map((p) => (
        <div key={p.label} className="flex justify-between">
          <span><span className="font-semibold">{p.count}×</span> {p.label}</span>
          <span>{p.price != null ? fmtGBP(p.price * p.count) : 'TBD'}</span>
        </div>
      ))}
    </div>
  );
};

const AreaCard = ({ area, idx, onChange, onRemove, canRemove }) => {
  const { FINISHES, WAXES, SEALERS, PIGMENTS } = useLive();
  const target = (parseFloat(area.areaM2) || 0) * (1 + area.wastagePct / 100);
  const finish = FINISHES.find((f) => f.id === area.finishId);
  const wax = WAXES.find((w) => w.id === area.waxId);
  const sealer = SEALERS.find((s) => s.id === area.sealerId);

  const finishResult = useMemo(
    () => (finish && target > 0 ? pickPacks(finish.packs, target) : null),
    [finish, target]
  );
  const waxResult = useMemo(
    () => (wax && target > 0 && wax.packs.every((p) => p.coverage_m2) ? pickPacks(wax.packs, target) : null),
    [wax, target]
  );
  const sealerResult = useMemo(
    () => (sealer && target > 0 && sealer.packs.every((p) => p.coverage_m2) ? pickPacks(sealer.packs, target) : null),
    [sealer, target]
  );

  const areaCost = (finishResult?.totalCost || 0)
    + (waxResult?.totalCost || 0)
    + (sealerResult?.totalCost || 0)
    + (area.pigmentSize ? (PIGMENTS[area.pigmentSize]?.price || 0) * (area.pigmentQty || 1) : 0);

  const update = (patch) => onChange({ ...area, ...patch });

  return (
    <Card className="p-5 border-2 border-[#D5AB09]/30">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg flex items-center justify-center text-white font-bold text-sm" style={{ backgroundColor: PRODECOR_PRIMARY }}>
            {idx + 1}
          </div>
          <h3 className="font-bold text-gray-900">Area {idx + 1}</h3>
        </div>
        {canRemove && (
          <button
            onClick={onRemove}
            data-testid={`prodecor-area-${idx}-remove`}
            className="p-2 text-rose-600 hover:bg-rose-50 rounded-lg transition-colors"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
        <div>
          <Label className="text-xs">Area name (optional)</Label>
          <Input
            data-testid={`prodecor-area-${idx}-name`}
            value={area.name}
            onChange={(e) => update({ name: e.target.value })}
            placeholder="e.g. Living Room"
          />
        </div>
        <div>
          <Label className="text-xs">Area (m²) *</Label>
          <Input
            data-testid={`prodecor-area-${idx}-m2`}
            type="number" min="0" step="0.1"
            value={area.areaM2}
            onChange={(e) => update({ areaM2: e.target.value })}
            placeholder="0"
          />
        </div>
        <div>
          <Label className="text-xs">Wastage</Label>
          <div className="flex gap-1 mt-1">
            {[0, 10, 20].map((w) => (
              <button
                key={w}
                onClick={() => update({ wastagePct: w })}
                className={`flex-1 py-1.5 text-xs rounded-lg border-2 transition-colors ${
                  area.wastagePct === w ? 'border-[#D5AB09] bg-[#D5AB09] text-white' : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                {w}%
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* FINISH */}
        <div>
          <Label className="text-xs uppercase tracking-wider text-gray-500 flex items-center gap-1">
            <Paintbrush className="w-3 h-3" /> Finish
          </Label>
          <select
            data-testid={`prodecor-area-${idx}-finish`}
            value={area.finishId || ''}
            onChange={(e) => update({ finishId: e.target.value || null })}
            className="w-full mt-1 px-3 py-2 rounded-lg border-2 border-gray-200 bg-white text-sm"
          >
            <option value="">— select finish —</option>
            {FINISHES.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
          </select>
          <PackList result={finishResult} label={finish?.name} />
        </div>

        {/* PIGMENT */}
        <div>
          <Label className="text-xs uppercase tracking-wider text-gray-500 flex items-center gap-1">
            <Droplet className="w-3 h-3" /> Pigment (optional)
          </Label>
          <select
            data-testid={`prodecor-area-${idx}-pigment`}
            value={area.pigmentSize || ''}
            onChange={(e) => update({ pigmentSize: e.target.value || null })}
            className="w-full mt-1 px-3 py-2 rounded-lg border-2 border-gray-200 bg-white text-sm"
          >
            <option value="">— no pigment —</option>
            <option value="small">20ml (stock) — {fmtGBP(PIGMENTS.small.price)}</option>
            <option value="large_stock">1L (stock colours) — {fmtGBP(PIGMENTS.large_stock.price)}</option>
            <option value="large_bespoke">1L (bespoke colour) — {fmtGBP(PIGMENTS.large_bespoke.price)}</option>
          </select>
          {area.pigmentSize && (
            <div className="grid grid-cols-2 gap-2 mt-2">
              {(area.pigmentSize === 'small' || area.pigmentSize === 'large_stock') ? (
                <select
                  value={area.pigmentColour}
                  onChange={(e) => update({ pigmentColour: e.target.value })}
                  className="px-3 py-2 rounded-lg border-2 border-gray-200 bg-white text-sm"
                >
                  {PIGMENTS[area.pigmentSize].colours.map((c) => <option key={c}>{c}</option>)}
                </select>
              ) : (
                <Input
                  value={area.pigmentColour}
                  onChange={(e) => update({ pigmentColour: e.target.value })}
                  placeholder="e.g. RAL 7016"
                />
              )}
              <Input
                type="number" min="1"
                value={area.pigmentQty}
                onChange={(e) => update({ pigmentQty: parseInt(e.target.value, 10) || 1 })}
              />
            </div>
          )}
        </div>

        {/* PROTECTION */}
        <div className="md:col-span-2">
          <Label className="text-xs uppercase tracking-wider text-gray-500 flex items-center gap-1">
            <Shield className="w-3 h-3" /> Protection (optional)
          </Label>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-1">
            <select
              data-testid={`prodecor-area-${idx}-wax`}
              value={area.waxId || ''}
              onChange={(e) => update({ waxId: e.target.value || null, sealerId: e.target.value ? null : area.sealerId, protectionType: e.target.value ? 'wax' : null })}
              className="px-3 py-2 rounded-lg border-2 border-gray-200 bg-white text-sm"
            >
              <option value="">— no wax —</option>
              {WAXES.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
            </select>
            <select
              data-testid={`prodecor-area-${idx}-sealer`}
              value={area.sealerId || ''}
              onChange={(e) => update({ sealerId: e.target.value || null, waxId: e.target.value ? null : area.waxId, protectionType: e.target.value ? 'sealer' : null, sealerVariant: null })}
              className="px-3 py-2 rounded-lg border-2 border-gray-200 bg-white text-sm"
            >
              <option value="">— no sealer —</option>
              {SEALERS.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </div>
          {sealer?.finishes && sealer.finishes.length > 1 && (
            <select
              value={area.sealerVariant || sealer.finishes[0]}
              onChange={(e) => update({ sealerVariant: e.target.value })}
              className="w-full mt-2 px-3 py-2 rounded-lg border-2 border-gray-200 bg-white text-sm"
            >
              {sealer.finishes.map((v) => <option key={v}>{v}</option>)}
            </select>
          )}
          {waxResult && <PackList result={waxResult} label={wax?.name} />}
          {sealerResult && <PackList result={sealerResult} label={`${sealer?.name}${area.sealerVariant ? ' — ' + area.sealerVariant : ''}`} />}
        </div>
      </div>

      <div className="flex justify-between items-center mt-4 pt-3 border-t border-gray-200">
        <span className="text-xs text-gray-500">Area subtotal</span>
        <span className="font-bold text-gray-900">{fmtGBP(areaCost)}</span>
      </div>
    </Card>
  );
};

export default function ProDecorCalculator() {
  const navigate = useNavigate();

  // Live-priced catalogue — fetches installer's assigned ProDecor tier and
  // overrides each pack's price with the tier value (fallbacks to the ex-VAT
  // hardcoded value if a SKU isn't in the pricelist yet).
  const [pricing, setPricing] = useState(null); // { tier, prices_by_name } | null
  const [pricingLoaded, setPricingLoaded] = useState(false);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/installer/prodecor-pricing`, { credentials: 'include' });
        if (res.ok && !cancelled) setPricing(await res.json());
      } catch (_e) {
        // Silent — fallback prices already display.
      } finally {
        if (!cancelled) setPricingLoaded(true);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const liveCtx = useMemo(() => {
    const map = pricing?.prices_by_name || null;
    return {
      PRIMERS: _applyLive(_PRIMERS, map),
      FINISHES: _applyLive(_FINISHES, map),
      PIGMENTS: _applyLive(_PIGMENTS, map),
      WAXES: _applyLive(_WAXES, map),
      SEALERS: _applyLive(_SEALERS, map),
      tier: pricing?.tier || null,
      loaded: pricingLoaded,
    };
  }, [pricing, pricingLoaded]);

  const { PRIMERS, FINISHES, PIGMENTS, WAXES, SEALERS, tier: liveTier } = liveCtx;
  const P100 = PRIMERS.find((p) => p.id === 'oxprime_p100');
  const P300 = PRIMERS.find((p) => p.id === 'oxprime_p300');
  const DPM = PRIMERS.find((p) => p.id === 'oxprimer_epoxy_dpm');

  const [areas, setAreas] = useState([blankArea()]);
  const [includeP100, setIncludeP100] = useState(true);
  const [includeP300, setIncludeP300] = useState(true);
  const [includeDPM, setIncludeDPM] = useState(false);
  // Per-primer override: null = use auto-calc; { [packLabel]: count } = use these quantities
  const [primerOverrides, setPrimerOverrides] = useState({
    [P100.id]: null,
    [P300.id]: null,
    [DPM.id]: null,
  });

  // Order-submission state
  const [customerReference, setCustomerReference] = useState('');
  const [deliveryNotes, setDeliveryNotes] = useState('');
  const [priority, setPriority] = useState('no_rush');
  const [voucher, setVoucher] = useState({ id: null, value: 0 });
  const [collectFromOx, setCollectFromOx] = useState(false); // if true, zero delivery
  const [isSaving, setIsSaving] = useState(false);

  // Delivery brackets (ex-VAT) — 1kg == 1 litre. Above 49 → pallet rate.
  const DELIVERY_BRACKETS = [
    { max: 15, price: 14.99 },
    { max: 25, price: 24.99 },
    { max: 49, price: 49.98 },
    { max: Infinity, price: 75.00 },
  ];
  const deliveryChargeFor = (weightKg) => {
    if (!weightKg || weightKg <= 0) return 0;
    return DELIVERY_BRACKETS.find((b) => weightKg <= b.max).price;
  };
  const packWeight = (product, pack) => {
    if (pack.weight_kg != null) return pack.weight_kg;
    if (product.unit === 'L' || product.unit === 'kg') return pack.size || 0;
    return 0;
  };

  const totalTarget = useMemo(() =>
    areas.reduce((sum, a) => {
      const m = parseFloat(a.areaM2) || 0;
      return sum + m * (1 + a.wastagePct / 100);
    }, 0),
    [areas]
  );

  const autoP100 = useMemo(() => (includeP100 && totalTarget > 0 ? pickPacks(P100.packs, totalTarget) : null), [includeP100, totalTarget]);
  const autoP300 = useMemo(() => (includeP300 && totalTarget > 0 ? pickPacks(P300.packs, totalTarget) : null), [includeP300, totalTarget]);
  const autoDPM = useMemo(() => (includeDPM && totalTarget > 0 ? pickPacks(DPM.packs, totalTarget) : null), [includeDPM, totalTarget]);

  /** Build the effective primer result from override map (or auto when override is null). */
  const buildOverrideResult = (primer, overrideMap) => {
    if (!overrideMap) return null;
    const picks = primer.packs
      .filter((pack) => (overrideMap[pack.label] || 0) > 0)
      .map((pack) => ({
        label: pack.label,
        size: pack.size,
        coverage_m2: pack.coverage_m2,
        price: pack.price,
        count: overrideMap[pack.label],
      }));
    if (picks.length === 0) return { picks: [], totalCoverage: 0, totalCost: 0, hasPrice: true };
    const totalCost = picks.reduce((s, p) => s + (p.price || 0) * p.count, 0);
    const totalCoverage = picks.reduce((s, p) => s + p.coverage_m2 * p.count, 0);
    return { picks, totalCoverage, totalCost, hasPrice: true };
  };

  const p100Result = primerOverrides[P100.id] ? buildOverrideResult(P100, primerOverrides[P100.id]) : autoP100;
  const p300Result = primerOverrides[P300.id] ? buildOverrideResult(P300, primerOverrides[P300.id]) : autoP300;
  const dpmResult  = primerOverrides[DPM.id]  ? buildOverrideResult(DPM,  primerOverrides[DPM.id])  : autoDPM;

  /** Start editing a primer — seed the override from the current auto-picks. */
  const beginPrimerEdit = (primer, autoResult) => {
    const seed = {};
    (autoResult?.picks || []).forEach((p) => { seed[p.label] = p.count; });
    setPrimerOverrides((prev) => ({ ...prev, [primer.id]: seed }));
  };

  const resetPrimer = (primer) => {
    setPrimerOverrides((prev) => ({ ...prev, [primer.id]: null }));
  };

  const setPrimerPackQty = (primer, packLabel, qty) => {
    setPrimerOverrides((prev) => {
      const current = prev[primer.id] || {};
      const next = { ...current, [packLabel]: Math.max(0, Math.floor(qty) || 0) };
      return { ...prev, [primer.id]: next };
    });
  };

  // Total weight (kg == L) of all picked packs across the order. Feeds the
  // Delivery Estimation card + bracket calc below.
  const totalWeightKg = useMemo(() => {
    let w = 0;
    const addFor = (product, packsResult) => {
      if (!packsResult) return;
      packsResult.picks.forEach((p) => {
        // packsResult.picks come back with the same shape as the source pack
        // (label, size, price, coverage_m2, count[, weight_kg]).
        w += packWeight(product, p) * (p.count || 0);
      });
    };
    if (p100Result) addFor(P100, p100Result);
    if (p300Result) addFor(P300, p300Result);
    if (dpmResult) addFor(DPM, dpmResult);

    areas.forEach((a) => {
      const target = (parseFloat(a.areaM2) || 0) * (1 + a.wastagePct / 100);
      const finish = FINISHES.find((f) => f.id === a.finishId);
      const wax = WAXES.find((wax) => wax.id === a.waxId);
      const sealer = SEALERS.find((s) => s.id === a.sealerId);
      if (finish && target > 0) addFor(finish, pickPacks(finish.packs, target));
      if (wax && target > 0 && wax.packs.every((p) => p.coverage_m2)) addFor(wax, pickPacks(wax.packs, target));
      if (sealer && target > 0 && sealer.packs.every((p) => p.coverage_m2)) addFor(sealer, pickPacks(sealer.packs, target));
      // Pigments — 20ml ≈ 0.02kg, 1L ≈ 1kg
      if (a.pigmentSize) {
        const pig = PIGMENTS[a.pigmentSize];
        const wPerUnit = (pig?.size_ml || 0) / 1000;
        w += wPerUnit * (a.pigmentQty || 1);
      }
    });
    return Math.round(w * 100) / 100;
  }, [areas, p100Result, p300Result, dpmResult]); // eslint-disable-line react-hooks/exhaustive-deps

  const deliveryCharge = useMemo(
    () => (collectFromOx ? 0 : deliveryChargeFor(totalWeightKg)),
    [totalWeightKg, collectFromOx], // eslint-disable-line react-hooks/exhaustive-deps
  );

  const deliveryBracketLabel = useMemo(() => {
    if (totalWeightKg <= 0) return '—';
    if (totalWeightKg <= 15) return 'Up to 15kg / 15L';
    if (totalWeightKg <= 25) return '15–25kg / 15–25L';
    if (totalWeightKg <= 49) return '25–49kg / 25–49L';
    return 'Over 49kg / 49L (pallet rate)';
  }, [totalWeightKg]);

  // Compute total subtotal (areas + primers + delivery)
  const grandTotal = useMemo(() => {
    let total = 0;
    [p100Result, p300Result, dpmResult].forEach((r) => { if (r) total += r.totalCost; });

    areas.forEach((a) => {
      const target = (parseFloat(a.areaM2) || 0) * (1 + a.wastagePct / 100);
      const finish = FINISHES.find((f) => f.id === a.finishId);
      const wax = WAXES.find((w) => w.id === a.waxId);
      const sealer = SEALERS.find((s) => s.id === a.sealerId);
      if (finish && target > 0) total += pickPacks(finish.packs, target).totalCost;
      if (wax && target > 0 && wax.packs.every((p) => p.coverage_m2)) total += pickPacks(wax.packs, target).totalCost;
      if (sealer && target > 0 && sealer.packs.every((p) => p.coverage_m2)) total += pickPacks(sealer.packs, target).totalCost;
      if (a.pigmentSize) total += (PIGMENTS[a.pigmentSize]?.price || 0) * (a.pigmentQty || 1);
    });
    return total + deliveryCharge;
  }, [areas, p100Result, p300Result, dpmResult, deliveryCharge]);

  const addArea = () => setAreas((prev) => [...prev, blankArea()]);
  const updateArea = (idx) => (next) => setAreas((prev) => prev.map((a, i) => (i === idx ? next : a)));
  const removeArea = (idx) => () => setAreas((prev) => prev.filter((_, i) => i !== idx));

  /** Build the order payload sent to the backend. */
  const buildOrderPayload = (status) => {
    const itemsForArea = (a) => {
      const items = [];
      const target = (parseFloat(a.areaM2) || 0) * (1 + a.wastagePct / 100);
      const finish = FINISHES.find((f) => f.id === a.finishId);
      const wax = WAXES.find((w) => w.id === a.waxId);
      const sealer = SEALERS.find((s) => s.id === a.sealerId);

      if (finish && target > 0) {
        const r = pickPacks(finish.packs, target);
        r.picks.forEach((p) => items.push({
          category: 'finish', product_id: finish.id, product_name: finish.name,
          pack_label: p.label, qty: p.count, unit_price: p.price,
          line_total: (p.price || 0) * p.count, coverage_m2: p.coverage_m2 * p.count,
          area_id: a.id,
        }));
      }
      if (a.pigmentSize) {
        const pig = PIGMENTS[a.pigmentSize];
        items.push({
          category: 'pigment',
          product_id: a.pigmentSize,
          product_name: `${pig.label} — ${a.pigmentColour}`,
          pack_label: pig.label, qty: a.pigmentQty || 1, unit_price: pig.price,
          line_total: (pig.price || 0) * (a.pigmentQty || 1),
          area_id: a.id,
        });
      }
      if (wax && target > 0 && wax.packs.every((p) => p.coverage_m2)) {
        const r = pickPacks(wax.packs, target);
        r.picks.forEach((p) => items.push({
          category: 'wax', product_id: wax.id, product_name: wax.name,
          pack_label: p.label, qty: p.count, unit_price: p.price,
          line_total: (p.price || 0) * p.count, coverage_m2: p.coverage_m2 * p.count,
          area_id: a.id,
        }));
      }
      if (sealer && target > 0 && sealer.packs.every((p) => p.coverage_m2)) {
        const r = pickPacks(sealer.packs, target);
        const variant = a.sealerVariant || (sealer.finishes ? sealer.finishes[0] : null);
        r.picks.forEach((p) => items.push({
          category: 'sealer', product_id: sealer.id,
          product_name: variant ? `${sealer.name} (${variant})` : sealer.name,
          pack_label: p.label, qty: p.count, unit_price: p.price,
          line_total: (p.price || 0) * p.count, coverage_m2: p.coverage_m2 * p.count,
          area_id: a.id,
        }));
      }
      return items;
    };

    const items = [];
    if (p100Result) p100Result.picks.forEach((p) => items.push({
      category: 'primer', product_id: P100.id, product_name: P100.name,
      pack_label: p.label, qty: p.count, unit_price: p.price,
      line_total: (p.price || 0) * p.count, coverage_m2: p.coverage_m2 * p.count,
    }));
    if (p300Result) p300Result.picks.forEach((p) => items.push({
      category: 'primer', product_id: P300.id, product_name: P300.name,
      pack_label: p.label, qty: p.count, unit_price: p.price,
      line_total: (p.price || 0) * p.count, coverage_m2: p.coverage_m2 * p.count,
    }));
    if (dpmResult) dpmResult.picks.forEach((p) => items.push({
      category: 'primer', product_id: DPM.id, product_name: DPM.name,
      pack_label: p.label, qty: p.count, unit_price: p.price,
      line_total: (p.price || 0) * p.count, coverage_m2: p.coverage_m2 * p.count,
    }));
    areas.forEach((a) => items.push(...itemsForArea(a)));

    return {
      status,
      customer_reference: customerReference,
      delivery_notes: deliveryNotes,
      priority,
      fulfilment: collectFromOx ? 'collect' : 'delivery',
      delivery: {
        total_weight_kg: totalWeightKg,
        bracket_label: deliveryBracketLabel,
        charge_ex_vat: deliveryCharge,
      },
      areas: areas.map((a) => ({
        id: a.id, name: a.name, area_m2: parseFloat(a.areaM2) || 0,
        wastage_pct: a.wastagePct, finish_id: a.finishId,
        pigment_size: a.pigmentSize, pigment_qty: a.pigmentQty, pigment_colour: a.pigmentColour,
        wax_id: a.waxId, sealer_id: a.sealerId, sealer_variant: a.sealerVariant,
      })),
      include_p100: includeP100,
      include_p300: includeP300,
      include_dpm: includeDPM,
      items,
      total: grandTotal,
      voucher_id: voucher.id || undefined,
    };
  };
  const liveVoucherItems = buildOrderPayload('draft').items.map((it) => ({ name: it.product_name, qty: it.qty }));
  const voucherOff = Math.min(voucher.value || 0, grandTotal);

  const submitOrder = async (status) => {
    if (totalTarget <= 0) {
      toast.error('Add an area first.');
      return;
    }
    const hasFinish = areas.some((a) => a.finishId);
    if (status === 'submitted' && !hasFinish && !includeP100 && !includeP300 && !includeDPM) {
      toast.error('Pick at least one finish or primer before submitting.');
      return;
    }

    setIsSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/prodecor-orders`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(buildOrderPayload(status)),
      });
      if (!res.ok) throw new Error(formatApiError(await res.json()) || 'Order failed');
      const data = await res.json();
      const saved = (data.vouchers_applied || []).reduce((a, v) => a + Number(v.amount || 0), 0);
      if (saved > 0) toast.success(`Order ${data.reference} submitted! Your training voucher saved you ${fmtGBP(saved)} — to pay ${fmtGBP(Number(data.total || 0) - saved)} excl. VAT.`, { duration: 8000 });
      else if (data.voucher_error) toast.warning(`Order ${data.reference} submitted, but the voucher wasn't applied: ${data.voucher_error}`, { duration: 9000 });
      else toast.success(status === 'submitted' ? `Order ${data.reference} submitted!` : `Draft ${data.reference} saved.`);
      navigate('/prodecor/orders');
    } catch (err) {
      toast.error(err.message);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <LivePricingCtx.Provider value={liveCtx}>
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="text-white" style={{ background: `linear-gradient(135deg, ${PRODECOR_SECONDARY}, #4a3a0d)` }}>
        <div className="max-w-7xl mx-auto px-3 sm:px-4 py-4 sm:py-5">
          <div className="flex items-center gap-2 sm:gap-4">
            <button onClick={() => navigate(-1)} data-testid="prodecor-calc-back-btn" className="p-2 hover:bg-white/10 rounded-lg shrink-0">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div className="flex-1 min-w-0">
              <h1 className="text-base sm:text-xl md:text-2xl font-bold truncate">ProDecor — Order Builder</h1>
              <p className="hidden sm:block text-white/70 text-sm">Add areas, the cheapest pack combination is worked out for you.</p>
            </div>
            <div className="flex gap-2 shrink-0">
              <Button
                onClick={() => navigate('/prodecor/orders')}
                variant="outline"
                size="sm"
                className="bg-white/10 border-white/30 text-white hover:bg-white/20 px-2 sm:px-3"
                aria-label="Material Orders"
              >
                <FileText className="w-4 h-4 sm:mr-1" />
                <span className="hidden sm:inline">Material Orders</span>
              </Button>
              <Button
                onClick={() => navigate('/')}
                variant="outline"
                size="sm"
                className="bg-white/10 border-white/30 text-white hover:bg-white/20 px-2 sm:px-3"
                aria-label="Home"
              >
                <Home className="w-4 h-4 sm:mr-1" />
                <span className="hidden sm:inline">Home</span>
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6 space-y-4">
        {/* Areas */}
        {areas.map((a, i) => (
          <AreaCard
            key={a.id}
            area={a}
            idx={i}
            onChange={updateArea(i)}
            onRemove={removeArea(i)}
            canRemove={areas.length > 1}
          />
        ))}

        <button
          onClick={addArea}
          data-testid="prodecor-add-area"
          className="w-full p-4 border-2 border-dashed border-gray-300 rounded-xl hover:border-[#D5AB09] hover:bg-[#D5AB09]/5 transition-colors text-gray-600 font-medium flex items-center justify-center gap-2"
        >
          <Plus className="w-5 h-5" /> Add another area
        </button>

        {/* PRIMERS panel */}
        <Card className="p-5 border-2 border-[#7A0B7A]/30">
          <div className="flex items-center gap-3 mb-3">
            <div className="p-2.5 rounded-xl text-white" style={{ backgroundColor: '#7A0B7A' }}>
              <Layers className="w-5 h-5" />
            </div>
            <div>
              <h3 className="font-bold text-gray-900">Primers (project-wide)</h3>
              <p className="text-xs text-gray-500">
                Spec: every finish needs one coat of <strong>P100</strong> + one coat of <strong>P300</strong>. Uncheck either if you already have stock.
              </p>
            </div>
          </div>
          <div className="text-sm text-gray-600 mb-3">
            Total primer coverage required: <span className="font-bold text-gray-900">{totalTarget.toFixed(1)} m²</span>
            <span className="ml-2 text-xs text-gray-500">— values below are auto-calculated, tap &quot;Customise&quot; to override.</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {[
              { primer: P100, include: includeP100, set: setIncludeP100, auto: autoP100, result: p100Result, label: 'P100 (1st coat)' },
              { primer: P300, include: includeP300, set: setIncludeP300, auto: autoP300, result: p300Result, label: 'P300 (2nd coat)' },
              { primer: DPM, include: includeDPM, set: setIncludeDPM, auto: autoDPM, result: dpmResult, label: 'Epoxy DPM (damp-proof, optional)' },
            ].map(({ primer, include, set, auto, result, label }) => {
              const isOverridden = primerOverrides[primer.id] !== null;
              const lineCoverage = result?.totalCoverage || 0;
              const shortfall = totalTarget - lineCoverage;
              return (
                <div key={primer.id} className={`p-3 rounded-lg border-2 ${include ? 'border-[#7A0B7A] bg-[#7A0B7A]/5' : 'border-gray-200'}`}>
                  <div className="flex items-center justify-between gap-2">
                    <label className="flex items-center gap-2 cursor-pointer flex-1 min-w-0">
                      <input
                        type="checkbox"
                        checked={include}
                        onChange={(e) => {
                          set(e.target.checked);
                          if (!e.target.checked) resetPrimer(primer);
                        }}
                        data-testid={`prodecor-include-${primer.id}`}
                        className="w-4 h-4"
                      />
                      <span className="font-medium text-sm truncate">{label}</span>
                    </label>
                    {include && (
                      isOverridden ? (
                        <button
                          type="button"
                          onClick={() => resetPrimer(primer)}
                          data-testid={`prodecor-reset-${primer.id}`}
                          className="text-[10px] uppercase tracking-wider font-bold text-[#7A0B7A] hover:underline"
                        >
                          Reset to auto
                        </button>
                      ) : (
                        <button
                          type="button"
                          onClick={() => beginPrimerEdit(primer, auto)}
                          data-testid={`prodecor-customise-${primer.id}`}
                          className="text-[10px] uppercase tracking-wider font-bold text-gray-500 hover:text-[#7A0B7A]"
                        >
                          Customise
                        </button>
                      )
                    )}
                  </div>

                  {include && !isOverridden && <PackList result={result} />}

                  {include && isOverridden && (
                    <div className="mt-2 space-y-1.5">
                      {primer.packs.map((pack) => {
                        const qty = (primerOverrides[primer.id] || {})[pack.label] || 0;
                        return (
                          <div key={pack.label} className="flex items-center justify-between gap-2 text-xs">
                            <div className="flex-1 min-w-0">
                              <div className="font-medium text-gray-800 truncate">{pack.label}</div>
                              <div className="text-[10px] text-gray-500">covers {pack.coverage_m2}m² · {pack.price != null ? fmtGBP(pack.price) : 'TBD'}</div>
                            </div>
                            <div className="flex items-center gap-1">
                              <button
                                type="button"
                                onClick={() => setPrimerPackQty(primer, pack.label, qty - 1)}
                                disabled={qty <= 0}
                                className="w-6 h-6 rounded border border-gray-300 text-gray-700 disabled:opacity-40 hover:bg-gray-100"
                                data-testid={`prodecor-${primer.id}-${pack.label}-minus`}
                              >−</button>
                              <input
                                type="number"
                                min="0"
                                value={qty}
                                onChange={(e) => setPrimerPackQty(primer, pack.label, parseInt(e.target.value, 10))}
                                data-testid={`prodecor-${primer.id}-${pack.label}-qty`}
                                className="w-10 h-6 text-center rounded border border-gray-300 text-xs"
                              />
                              <button
                                type="button"
                                onClick={() => setPrimerPackQty(primer, pack.label, qty + 1)}
                                className="w-6 h-6 rounded border border-gray-300 text-gray-700 hover:bg-gray-100"
                                data-testid={`prodecor-${primer.id}-${pack.label}-plus`}
                              >+</button>
                            </div>
                          </div>
                        );
                      })}
                      <div className="pt-1 mt-1 border-t border-[#7A0B7A]/20 text-[11px] flex justify-between text-gray-600">
                        <span>Covers <strong>{lineCoverage.toFixed(1)}m²</strong></span>
                        <span className="font-bold text-gray-900">{fmtGBP(result?.totalCost || 0)}</span>
                      </div>
                      {shortfall > 0.05 && (
                        <div className="text-[10px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1">
                          ⓘ {shortfall.toFixed(1)}m² short of target — add another pack.
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </Card>

        {/* Order details */}
        <Card className="p-5">
          <h3 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
            <MapPin className="w-5 h-5 text-[#D5AB09]" /> Order Details
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs">Customer / project reference (optional)</Label>
              <Input
                data-testid="prodecor-order-ref"
                value={customerReference}
                onChange={(e) => setCustomerReference(e.target.value)}
                placeholder="e.g. Smith — Living Room job"
              />
            </div>
            <div>
              <Label className="text-xs">Delivery notes (optional)</Label>
              <Input
                data-testid="prodecor-order-notes"
                value={deliveryNotes}
                onChange={(e) => setDeliveryNotes(e.target.value)}
                placeholder="e.g. Leave at side gate if no answer"
              />
            </div>
          </div>
          <p className="text-xs text-gray-500 mt-2">
            Delivery address will be pulled from your <a href="/settings" className="text-[#2F5E96] underline">Business Profile</a>.
          </p>
        </Card>

        {/* PRIORITY SELECTOR */}
        <PrioritySelector value={priority} onChange={setPriority} />

        {/* DELIVERY ESTIMATION */}
        <Card className="p-5 border-2 border-[#D5AB09]/40 bg-[#D5AB09]/5" data-testid="prodecor-delivery-card">
          <h3 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
            <MapPin className="w-5 h-5 text-[#D5AB09]" /> Delivery Estimation
          </h3>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-600">Total weight:</span>
              <span className="font-bold text-gray-900 text-lg">
                {totalWeightKg.toFixed(2)} kg / L
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Delivery bracket:</span>
              <span className="text-gray-800">{deliveryBracketLabel}</span>
            </div>

            {/* Fulfilment toggle — zeroes out the delivery charge when collecting. */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 pt-2 border-t border-[#D5AB09]/30">
              <span className="text-gray-600">Fulfilment:</span>
              <div
                className="inline-flex rounded-full bg-white border border-[#D5AB09]/50 p-0.5 self-start sm:self-auto"
                data-testid="prodecor-delivery-toggle"
              >
                <button
                  type="button"
                  onClick={() => setCollectFromOx(false)}
                  className={`px-3 py-1 rounded-full text-xs font-semibold transition-colors ${!collectFromOx ? 'bg-[#D5AB09] text-[#2B2101]' : 'text-[#7a6300] hover:bg-[#D5AB09]/10'}`}
                  data-testid="prodecor-delivery-toggle-deliver"
                >
                  Deliver
                </button>
                <button
                  type="button"
                  onClick={() => setCollectFromOx(true)}
                  className={`px-3 py-1 rounded-full text-xs font-semibold transition-colors ${collectFromOx ? 'bg-[#D5AB09] text-[#2B2101]' : 'text-[#7a6300] hover:bg-[#D5AB09]/10'}`}
                  data-testid="prodecor-delivery-toggle-collect"
                >
                  Collect from Ox
                </button>
              </div>
            </div>

            <div className="flex justify-between pt-1">
              <span className="text-gray-600">Delivery charge (ex-VAT):</span>
              <span className="font-bold text-gray-900">
                {collectFromOx ? 'No charge — collecting' : fmtGBP(deliveryCharge)}
              </span>
            </div>
            <p className="text-[11px] text-gray-500 italic pt-1">
              £14.99 up to 15kg · £24.99 (15–25kg) · £49.98 (25–49kg) · £75 over 49kg — 1kg = 1L. VAT added at checkout.
            </p>
          </div>
        </Card>

        <VoucherRail brand="prodecor" items={liveVoucherItems} selectedId={voucher.id} onSelect={(id, value) => setVoucher({ id, value })} accent={PRODECOR_PRIMARY} />
        {/* TOTAL + CTA */}
        <Card className="p-5" style={{ backgroundColor: PRODECOR_SECONDARY }}>
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <div className="text-xs uppercase tracking-wider text-white/60">Order total (excl. VAT)</div>
              <div className="text-3xl font-bold" style={{ color: PRODECOR_PRIMARY }}>{fmtGBP(grandTotal)}</div>
              {voucherOff > 0 && <div className="text-xs font-semibold mt-1" style={{ color: PRODECOR_PRIMARY }} data-testid="pd-voucher-line">Training voucher − {fmtGBP(voucherOff)} · to pay {fmtGBP(grandTotal - voucherOff)}</div>}
              {liveTier && (
                <div className="text-[11px] text-white/50 mt-1" data-testid="prodecor-tier-badge">
                  Priced at your <span className="font-semibold text-white/80">{liveTier.label}</span> tier
                </div>
              )}
            </div>
            <div className="flex gap-2">
              <Button
                onClick={() => submitOrder('draft')}
                disabled={isSaving || totalTarget <= 0}
                variant="outline"
                className="bg-white/10 border-white/30 text-white hover:bg-white/20"
                data-testid="prodecor-save-draft-btn"
              >
                <Save className="w-4 h-4 mr-1" /> Save Draft
              </Button>
              <Button
                onClick={() => submitOrder('submitted')}
                disabled={isSaving || totalTarget <= 0}
                style={{ backgroundColor: PRODECOR_PRIMARY, color: PRODECOR_SECONDARY }}
                className="font-bold hover:opacity-90"
                data-testid="prodecor-submit-btn"
              >
                <Send className="w-4 h-4 mr-1" /> Submit Order
              </Button>
            </div>
          </div>
        </Card>
      </div>
    </div>
    </LivePricingCtx.Provider>
  );
}
